import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { categories, mainSiteUrl } from "@/mocks/categories";

const navLinks = [
  { key: "nav.home", to: "/" },
  { key: "nav.spreadsheet", to: "/#categories" },
  { key: "nav.reviews", to: "/#review" },
  { key: "nav.shipping", to: "/#shipping" },
  { key: "nav.guides", to: "/#guides" },
  { key: "nav.safety", to: "/#safety" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur transition-shadow ${
        scrolled ? "shadow-[0_1px_0_0_oklch(var(--background-200))]" : ""
      }`}
    >
      <nav className="mx-auto flex h-16 w-full items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-links-line text-lg" aria-hidden="true"></i>
          </span>
          <span className="text-lg font-bold tracking-tight text-foreground-950">USFansLink</span>
        </Link>

        <div className="hidden items-center gap-0.5 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.key}
              to={link.to}
              className="whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              {t(link.key)}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <LanguageSwitcher />
          <Link
            to="/usfans-spreadsheet-guide"
            className="whitespace-nowrap rounded-full border border-primary-300 px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
          >
            {t("nav.shoppingGuide")}
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("nav.cta")}
          </a>
        </div>

        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-800 lg:hidden"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} aria-hidden="true"></i>
        </button>
      </nav>

      {menuOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
              >
                {t(link.key)}
              </Link>
            ))}
            <Link
              to="/usfans-spreadsheet-guide"
              className="rounded-lg px-3 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-50"
            >
              {t("nav.shoppingGuide")}
            </Link>
          </div>

          <div className="mt-3 border-t border-background-200 pt-3">
            <p className="px-3 pb-2 text-xs font-bold uppercase tracking-wider text-foreground-500">
              {t("footer.navCategories")}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-2 py-2 transition-colors hover:border-primary-300"
                >
                  <img src={c.img} alt={t(c.key)} className="h-8 w-8 rounded-md object-cover object-top" />
                  <span className="truncate text-xs font-semibold text-foreground-800">{t(c.key)}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
            <LanguageSwitcher />
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}